import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Library, Play } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Field, Input, Select, Toggle } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { ScriptRunLogDialog } from '@/components/jobs/LogView';
import { formatRelative } from '@/lib/format';
import { Link } from 'react-router-dom';

/**
 * Apakah parameter ini menanyakan NAMA KOLOM dari data yang dipilih.
 *
 * Dikenali dari namanya, bukan dari tipe tersendiri. Alasannya praktis:
 * template yang sudah tersemai di produksi bertipe `text`, dan menambah tipe
 * baru berarti seluruh baris lama harus dipindahkan lebih dulu supaya
 * dropdownnya muncul. Penamaannya sendiri sudah konsisten di seluruh katalog.
 */
const adalahParamKolom = (param) =>
  param.type === 'kolom' || /^kolom(_|$)/.test(param.name ?? '');

/** Satu isian parameter, dirender sesuai tipenya. */
const IsianParam = ({ param, nilai, onUbah, kolomTersedia, kolomSedangDimuat }) => {
  const label = param.label || param.name;

  /**
   * Kolom dari berkas yang dipilih, sebagai dropdown.
   *
   * Selama jawabannya harus DIKETIK, pengguna wajib mengingat ejaan persis
   * nama kolom hasil scraping, dan salah satu huruf menghasilkan galat yang
   * menyebut kolom tidak ditemukan tanpa memberi tahu apa saja yang ada.
   *
   * Kalau kolomnya belum bisa dibaca, isian kembali menjadi kotak ketik.
   * Dropdown kosong yang tidak bisa dipilih apa pun jauh lebih buruk daripada
   * kotak ketik, karena ia menghalangi jalan tanpa menawarkan jalan lain.
   */
  if (adalahParamKolom(param)) {
    if (kolomSedangDimuat) {
      return (
        <Field label={label} required={param.required} hint="Membaca kolom dari berkas...">
          {(props) => <Select {...props} disabled value=""><option>Memuat...</option></Select>}
        </Field>
      );
    }

    if (kolomTersedia?.length) {
      return (
        <Field label={label} required={param.required} hint={param.help}>
          {(props) => (
            <Select {...props} value={nilai ?? ''} onChange={(e) => onUbah(e.target.value)}>
              <option value="">{param.required ? 'Pilih kolom...' : 'Tidak dipakai'}</option>
              {kolomTersedia.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    }
  }

  if (param.type === 'boolean') {
    return (
      <Toggle
        checked={nilai === true || nilai === 'true'}
        onChange={(v) => onUbah(v)}
        label={label}
        description={param.help}
      />
    );
  }

  return (
    <Field label={label} required={param.required} hint={param.help}>
      {(props) =>
        param.type === 'pilihan' ? (
          <Select {...props} value={nilai ?? ''} onChange={(e) => onUbah(e.target.value)}>
            {!param.defaultValue && <option value="">Pilih...</option>}
            {param.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            {...props}
            type={param.type === 'number' ? 'number' : 'text'}
            value={nilai ?? ''}
            placeholder={param.defaultValue || undefined}
            onChange={(e) => onUbah(e.target.value)}
          />
        )
      }
    </Field>
  );
};

/**
 * Mode Pustaka: scraping dari template siap pakai buatan dev.
 *
 * User memilih website, lalu bagiannya, lalu mengisi formulir yang dirender
 * dari deklarasi parameter template. Skripnya berjalan di server (runner lab)
 * dan hasilnya mendarat di Penyimpanan Pribadi pada folder pilihan user.
 */
/**
 * Hasil pemeriksaan kualitas folder.
 *
 * ── Kenapa ringkasan sebaris DAN popup, bukan salah satu ────────────────
 *
 * Popup menyampaikan masalahnya dengan tegas pada saat yang tepat: begitu
 * folder dipilih dan ternyata cacat. Tetapi popup yang sudah ditutup tidak
 * meninggalkan jejak, dan orang yang menutupnya lalu kembali beberapa menit
 * kemudian akan menemukan tombol Jalankan mati tanpa tahu sebabnya.
 *
 * Ringkasan sebaris yang menetap menjawab itu, dan tetap menyediakan jalan
 * membuka rinciannya lagi.
 */
const JUDUL_MASALAH = {
  'folder-kosong': 'Folder masih kosong',
  'kolom-beda': 'Kolomnya tidak sama',
  'berkas-kosong': 'Ada berkas yang tidak berisi',
  'pola-nama': 'Ada berkas yang tidak sepola',
  'format-campur': 'Formatnya bercampur',
  'terlalu-banyak': 'Sebagian berkas dilewati',
};

/** Baris pendek yang menetap di bawah pemilih folder. */
const RingkasPeriksa = ({ hasil, sedangMemuat, onLihat }) => {
  if (sedangMemuat) {
    return <p className="text-xs text-[var(--text-muted)]">Memeriksa isi folder...</p>;
  }
  if (!hasil) return null;

  if (hasil.siap) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-success-700 dark:text-success-500">
        <CheckCircle2 className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        <span>
          {hasil.jumlahBerkas} berkas siap dibaca sebagai satu kesatuan
          {hasil.kolom.length > 0 ? `, kolomnya sama: ${hasil.kolom.join(', ')}` : ''}
        </span>
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="flex items-start gap-1.5 text-xs font-semibold text-danger-700 dark:text-danger-500">
        <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        <span>Folder ini belum siap dianalisa.</span>
      </p>
      <button
        type="button"
        onClick={onLihat}
        className="text-xs font-bold text-danger-700 underline underline-offset-2 hover:no-underline dark:text-danger-500"
      >
        Lihat apa yang salah
      </button>
    </div>
  );
};

/**
 * Isi popup: seluruh masalah, beserta nama berkasnya.
 *
 * "Folder tidak bisa dianalisa" tidak memberi tahu apa yang harus dikerjakan.
 * Yang bisa ditindaklanjuti hanyalah nama berkasnya dan apa yang salah
 * padanya, jadi itu yang ditampilkan.
 */
const RincianPeriksa = ({ hasil }) => {
  if (!hasil) return null;

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
        Menggabungkan berkas yang bentuknya berbeda menghasilkan angka yang salah tanpa
        memunculkan galat apa pun: kolom kosong di sebagian baris, lalu rata-rata yang dihitung
        dari separuh data. Karena itu analisanya ditahan sampai yang di bawah ini dibereskan.
      </p>

      <ul className="space-y-3">
        {hasil.masalah.map((m) => (
          <li
            key={m.jenis}
            className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-4"
          >
            <p className="text-sm font-bold text-[var(--text-primary)]">
              {JUDUL_MASALAH[m.jenis] ?? m.jenis}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">
              {m.pesan} {m.saran}
            </p>
            {m.berkas?.length > 0 && (
              <ul className="mt-2 space-y-1 border-t border-[var(--border-subtle)] pt-2">
                {m.berkas.map((b) => (
                  <li
                    key={b}
                    className="font-mono text-[11px] leading-relaxed break-words text-[var(--text-muted)]"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {hasil.berkas?.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Isi folder ({hasil.jumlahBerkas} berkas)
          </p>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-[var(--border-subtle)]">
            {hasil.berkas.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-3 py-1.5 text-xs last:border-0"
              >
                <span className="truncate font-mono text-[var(--text-secondary)]">{b.nama}</span>
                <span className="shrink-0 tabular text-[var(--text-muted)]">
                  {b.kolom} kolom{b.baris != null ? ` · ${b.baris} baris` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Nama dropdown pemilih template, per kategori.
 *
 * "Bagian Scraping" salah di mana pun selain scraping, dan yang membacanya di
 * halaman EDA wajar bertanya scraping yang mana.
 */
const LABEL_PILIHAN = {
  eda: 'Jenis Analisa',
  analytics: 'Jenis Laporan',
  tools: 'Alat',
};

/**
 * Judul kelompok pada dropdown.
 *
 * Nilai `section` disimpan huruf kecil tanpa spasi karena ia bagian dari
 * kunci unik template. Yang dibaca pengguna tidak perlu ikut menanggung itu.
 */
const JUDUL_BAGIAN = {
  kualitas: 'Memeriksa kualitas data',
  ringkasan: 'Gambaran umum',
  harga: 'Harga',
  waktu: 'Pola dari waktu ke waktu',
  teks: 'Kolom teks',
  kategori: 'Perbandingan antar kelompok',
  hubungan: 'Hubungan antar kolom',
};

export const TemplateMode = ({ category = 'scraping' }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: katalogData } = useQuery({
    queryKey: ['script-templates', category],
    queryFn: () => api.get('/script-templates', { params: { category } }),
  });
  const { data: fileData } = useQuery({
    queryKey: ['storage-files-pilih'],
    queryFn: () => api.get('/storage/files', { params: { limit: 100 } }),
  });
  const { data: jobData } = useQuery({
    queryKey: ['jobs-pilih'],
    queryFn: () => api.get('/jobs', { params: { limit: 100 } }),
  });
  const { data: folderData } = useQuery({
    queryKey: ['storage-folders-all'],
    queryFn: () => api.get('/storage/folders/all'),
  });
  const { data: runsData } = useQuery({
    queryKey: ['script-runs'],
    queryFn: () => api.get('/script-runs', { params: { limit: 10 } }),
    // Menyegarkan sendiri selama ada eksekusi yang belum selesai.
    refetchInterval: (query) => {
      const items = query.state.data?.data ?? [];
      return items.some((r) => r.status === 'queued' || r.status === 'running') ? 2000 : false;
    },
  });

  const katalog = katalogData?.data ?? [];
  const folders = folderData?.data ?? [];
  const riwayat = runsData?.data ?? [];
  const berkas = fileData?.data?.files ?? fileData?.data ?? [];
  const pekerjaan = jobData?.data ?? [];

  const [website, setWebsite] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [params, setParams] = useState({});
  const [outputFormat, setOutputFormat] = useState('csv');
  const [namaJob, setNamaJob] = useState('');
  const [maxRows, setMaxRows] = useState('');
  const [folderId, setFolderId] = useState('');

  const [inputId, setInputId] = useState('');

  /**
   * Kolom dari berkas yang sedang dipilih.
   *
   * Hanya diminta kalau template ini memang punya isian yang menanyakan nama
   * kolom. Membaca kepala berkas demi template yang tidak menanyakannya cuma
   * menambah satu perjalanan jaringan tanpa ada yang memakainya.
   */
  const { data: dataKolom, isFetching: kolomSedangDimuat } = useQuery({
    queryKey: ['kolom-berkas', inputId],
    queryFn: () => api.get(`/storage/files/${inputId}/kolom`),
    enabled: Boolean(inputId),
    staleTime: 5 * 60 * 1000,
  });
  const kolomTersedia = dataKolom?.data?.kolom ?? [];

  const [inputFolderId, setInputFolderId] = useState('');

  /** 'berkas' atau 'folder'. Hanya berarti untuk template `file-atau-folder`. */
  const [jenisSumber, setJenisSumber] = useState('berkas');
  const [periksaTerbuka, setPeriksaTerbuka] = useState(false);

  /**
   * Pemeriksaan kualitas folder, dijalankan begitu foldernya dipilih.
   *
   * Hasilnya menentukan boleh tidaknya tombol Jalankan ditekan. Server tetap
   * memeriksa ulang saat permintaan masuk: layar yang menahan tombol cuma
   * menyembunyikan jalannya, dan permintaan yang dikirim langsung ke API tetap
   * harus ditolak.
   */
  const { data: dataPeriksa, isFetching: periksaSedangJalan } = useQuery({
    queryKey: ['periksa-folder', inputFolderId],
    queryFn: () => api.get(`/storage/folders/${inputFolderId}/periksa`),
    enabled: Boolean(inputFolderId),
  });
  const hasilPeriksa = dataPeriksa?.data ?? null;

  /**
   * Popup dibuka SENDIRI begitu pemeriksaan selesai dan hasilnya gagal.
   *
   * Menunggu pengguna menekan tautan berarti sebagian orang akan menekan
   * Jalankan lebih dulu, menerima penolakan, lalu baru mencari sebabnya.
   * Masalah yang sudah diketahui sebaiknya disampaikan saat itu juga.
   *
   * Ditandai per folder supaya menutup popup tidak langsung membukanya lagi
   * pada render berikutnya.
   */
  const folderDiberitahu = useRef(null);
  useEffect(() => {
    if (!hasilPeriksa || periksaSedangJalan) return;
    if (hasilPeriksa.siap) return;
    if (folderDiberitahu.current === inputFolderId) return;
    folderDiberitahu.current = inputFolderId;
    setPeriksaTerbuka(true);
  }, [hasilPeriksa, periksaSedangJalan, inputFolderId]);

  const [logTerbuka, setLogTerbuka] = useState(null);

  const websites = useMemo(() => [...new Set(katalog.map((t) => t.website))], [katalog]);

  /**
   * Seluruh template kategori ini, dikelompokkan menurut bagiannya.
   *
   * Dipakai kategori selain scraping, yang cuma punya satu dropdown. Urutannya
   * mengikuti abjad bagian supaya letaknya tidak berpindah-pindah setiap kali
   * dev menambah satu template baru.
   */
  const kelompok = useMemo(() => {
    const peta = new Map();
    for (const t of katalog) {
      if (!peta.has(t.section)) peta.set(t.section, []);
      peta.get(t.section).push(t);
    }
    return [...peta.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [katalog]);
  const bagian = useMemo(
    () => katalog.filter((t) => t.website === website),
    [katalog, website],
  );
  const template = katalog.find((t) => t.id === templateId) ?? null;

  const bebasPilih = template?.inputType === 'file-atau-folder';
  const pakaiBerkas = template?.inputType === 'file' || (bebasPilih && jenisSumber === 'berkas');
  const pakaiFolder = template?.inputType === 'folder' || (bebasPilih && jenisSumber === 'folder');

  /**
   * Boleh tidaknya dijalankan.
   *
   * Untuk folder, "sudah dipilih" saja tidak cukup: pemeriksaannya harus
   * selesai DAN lulus. Membiarkan tombol aktif selagi pemeriksaan berjalan
   * membuat orang menekannya lalu menerima penolakan dari server, dan
   * penolakan yang datang belakangan selalu terasa seperti kerusakan.
   */
  const siapJalan = (() => {
    if (!template) return false;
    if (template.inputType === 'none') return true;
    if (pakaiFolder) {
      // Folder butuh lebih dari sekadar "sudah dipilih": pemeriksaannya harus
      // selesai DAN lulus. Membiarkan tombol aktif selagi pemeriksaan berjalan
      // membuat orang menekannya lalu menerima penolakan dari server, dan
      // penolakan yang datang belakangan selalu terasa seperti kerusakan.
      return Boolean(inputFolderId) && !periksaSedangJalan && hasilPeriksa?.siap === true;
    }
    if (pakaiBerkas) return Boolean(inputId);
    return Boolean(inputId);
  })();

  // Saat template berganti, isikan nilai bawaan parameternya.
  useEffect(() => {
    if (!template) return;
    setParams(
      Object.fromEntries(
        template.params.map((p) => [p.name, p.defaultValue || (p.type === 'boolean' ? false : '')]),
      ),
    );
    setOutputFormat(template.outputFormats[0] ?? 'csv');
    setMaxRows(String(template.maxRows));
    setNamaJob(`${template.website}-${template.section}`);
    setInputId('');
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const jalankan = useMutation({
    mutationFn: () =>
      api.post(`/script-templates/${templateId}/run`, {
        params,
        outputFormat,
        outputName: namaJob.trim() || undefined,
        inputFileId: pakaiBerkas ? inputId || undefined : undefined,
        inputJobId: template?.inputType === 'job' ? inputId || null : undefined,
        inputFolderId: pakaiFolder ? inputFolderId || undefined : undefined,
        maxRows: Number(maxRows) || undefined,
        folderId: folderId || null,
      }),
    onSuccess: () => {
      toast.success('Skrip masuk antrian', {
        description: 'Pantau statusnya di daftar riwayat di bawah.',
      });
      queryClient.invalidateQueries({ queryKey: ['script-runs'] });
    },
    onError: (err) =>
      toast.error('Gagal menjalankan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (katalog.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Library}
          title="Pustaka masih kosong"
          description="Belum ada template dari tim dev. Template yang mereka daftarkan akan muncul di sini sebagai formulir siap pakai, tanpa perlu selektor atau konfigurasi."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={category === "scraping" ? "Pustaka Scraping" : "Pustaka Analisa"}
          description={
            category === "scraping"
              ? "Pilih sumber datanya; formulirnya sudah disiapkan tim dev. Isi, jalankan, hasilnya masuk Penyimpanan Pribadi."
              : "Pilih data dan analisanya; formulirnya sudah disiapkan tim dev. Hasilnya berupa laporan berisi grafik dan angka."
          }
        />
        <CardBody className="space-y-4">
          <Field label="Nama Job" hint="Menjadi nama berkas hasil di Penyimpanan Pribadi.">
            {(props) => (
              <Input
                {...props}
                value={namaJob}
                placeholder="Misal: produk-elektronik-mingguan"
                onChange={(e) => setNamaJob(e.target.value)}
              />
            )}
          </Field>

          {/* Dua dropdown hanya masuk akal untuk scraping.

              Di sana "website" memang membedakan sesuatu: tokopedia berbeda
              dari shopee, dan bagiannya berbeda lagi. Di EDA dan Analytics
              tidak ada situs mana pun yang dituju, jadi dropdown pertama cuma
              berisi satu pilihan bernama "eda" - satu keputusan yang harus
              diambil pengguna tanpa ada yang bisa dipilih salah. */}
          {category === 'scraping' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Website" required>
                {(props) => (
                  <Select
                    {...props}
                    value={website}
                    onChange={(e) => {
                      setWebsite(e.target.value);
                      setTemplateId('');
                    }}
                  >
                    <option value="">Pilih website...</option>
                    {websites.map((w) => (
                      <option key={w} value={w}>
                        {w}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label="Bagian" required hint={template?.targetInfo}>
                {(props) => (
                  <Select
                    {...props}
                    value={templateId}
                    disabled={!website}
                    onChange={(e) => setTemplateId(e.target.value)}
                  >
                    <option value="">{website ? 'Pilih bagian...' : 'Pilih website dulu'}</option>
                    {bagian.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.section}: {t.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          ) : (
            <Field label={LABEL_PILIHAN[category] ?? 'Jenis Analisa'} required hint={template?.description ? undefined : template?.targetInfo}>
              {(props) => (
                <Select {...props} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  <option value="">Pilih analisa...</option>
                  {/* Dikelompokkan menurut pertanyaan yang dijawabnya, bukan
                      didaftar rata. Sembilan pilihan yang berderet tanpa
                      pengelompokan menuntut pembacanya membaca semuanya. */}
                  {kelompok.map(([bagianNama, daftar]) => (
                    <optgroup key={bagianNama} label={JUDUL_BAGIAN[bagianNama] ?? bagianNama}>
                      {daftar.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {/* Pemilih sumber: satu berkas, atau satu folder.

              Skrip analisanya tidak peduli datanya berasal dari satu berkas
              atau dua puluh. Yang peduli penggunanya: kadang ia punya satu
              berkas unggahan, kadang folder tujuan scheduler yang berisi
              potongan harian. */}
          {bebasPilih && (
            <Field label="Sumber Data" required>
              {() => (
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    ['berkas', 'Satu berkas', 'Berkas tunggal dari Penyimpanan Pribadi.'],
                    ['folder', 'Satu folder', 'Semua berkas di dalamnya dibaca sebagai satu.'],
                  ].map(([nilai, judul, keterangan]) => (
                    <button
                      key={nilai}
                      type="button"
                      onClick={() => {
                        setJenisSumber(nilai);
                        setInputId('');
                        setInputFolderId('');
                      }}
                      className={cn(
                        'rounded-xl border p-3 text-left transition-colors',
                        jenisSumber === nilai
                          ? 'border-accent-300 bg-accent-50 dark:border-accent-500/40 dark:bg-accent-500/10'
                          : 'border-[var(--border-subtle)] bg-[var(--surface-sunken)] hover:border-[var(--border-default)]',
                      )}
                    >
                      <span
                        className={cn(
                          'block text-sm font-bold',
                          jenisSumber === nilai
                            ? 'text-accent-700 dark:text-accent-300'
                            : 'text-[var(--text-primary)]',
                        )}
                      >
                        {judul}
                      </span>
                      <span className="mt-0.5 block text-xs text-[var(--text-muted)]">
                        {keterangan}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Field>
          )}

          {pakaiBerkas && (
            <Field
              label={bebasPilih ? 'Berkas' : 'Sumber Data'}
              required
              hint="Berkas dari Penyimpanan Pribadi yang akan diproses."
            >
              {(props) => (
                <Select {...props} value={inputId} onChange={(e) => setInputId(e.target.value)}>
                  <option value="">Pilih berkas...</option>
                  {berkas.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.filename}
                      {f.rowCount != null ? ` (${f.rowCount} baris)` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {pakaiFolder && (
            <div className="space-y-2">
              <Field
                label={bebasPilih ? 'Folder' : 'Sumber Data'}
                required
                hint="Seluruh berkas dalam folder ini dibaca sebagai satu kesatuan."
              >
                {(props) => (
                  <Select
                    {...props}
                    value={inputFolderId}
                    onChange={(e) => setInputFolderId(e.target.value)}
                  >
                    <option value="">Pilih folder...</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.path ?? f.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <RingkasPeriksa
                hasil={hasilPeriksa}
                sedangMemuat={periksaSedangJalan}
                onLihat={() => setPeriksaTerbuka(true)}
              />
            </div>
          )}

          {template?.inputType === 'job' && (
            <Field label="Sumber Data" required hint="Seluruh hasil pekerjaan ini menjadi masukan.">
              {(props) => (
                <Select {...props} value={inputId} onChange={(e) => setInputId(e.target.value)}>
                  <option value="">Pilih pekerjaan...</option>
                  {pekerjaan.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {template && (
            <>
              {template.description && (
                <p className="text-sm text-[var(--text-secondary)]">{template.description}</p>
              )}

              {template.params.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  {template.params.map((p) => (
                    <IsianParam
                      kolomTersedia={kolomTersedia}
                      kolomSedangDimuat={kolomSedangDimuat}
                      key={p.name}
                      param={p}
                      nilai={params[p.name]}
                      onUbah={(v) => setParams((prev) => ({ ...prev, [p.name]: v }))}
                    />
                  ))}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Format Output">
                  {(props) => (
                    <Select
                      {...props}
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                    >
                      {template.outputFormats.map((f) => (
                        <option key={f} value={f}>
                          {f.toUpperCase()}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Jumlah Data" hint={`Maksimal ${template.maxRows} baris.`}>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min="1"
                      max={template.maxRows}
                      value={maxRows}
                      onChange={(e) => setMaxRows(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Simpan ke Folder">
                  {(props) => (
                    <Select {...props} value={folderId} onChange={(e) => setFolderId(e.target.value)}>
                      <option value="">Akar penyimpanan</option>
                      {folders.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.path}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              <div className="flex justify-end">
                {/* Folder yang gagal pemeriksaan menahan tombolnya.

                    Server tetap memeriksa ulang saat permintaan masuk; ini
                    hanya supaya orang tidak menunggu eksekusi yang sudah pasti
                    ditolak. */}
                <Button
                  icon={Play}
                  loading={jalankan.isPending}
                  disabled={!siapJalan}
                  onClick={() => jalankan.mutate()}
                >
                  Jalankan
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Riwayat eksekusi hanya ditampilkan untuk kategori analisa.
          Di halaman Buat Scraping ia mengganggu: eksekusi yang sedang
          berjalan sudah punya rumahnya sendiri di Scraping Saat Ini, dan
          hasilnya berakhir di Penyimpanan Pribadi. */}
      {category !== 'scraping' && riwayat.length > 0 && (
        <Card>
          <CardHeader title="Riwayat Eksekusi" description="Sepuluh eksekusi terakhir organisasi ini." />
          <TableWrapper>
            <Table>
              <thead>
                <Tr>
                  <Th>Template</Th>
                  <Th>Status</Th>
                  <Th>Hasil</Th>
                  <Th>Kapan</Th>
                </Tr>
              </thead>
              <tbody>
                {riwayat.map((r) => (
                  <Tr key={r.id}>
                    <Td>
                      <button
                        type="button"
                        onClick={() => setLogTerbuka(r.id)}
                        className="text-left font-medium text-[var(--text-primary)] hover:underline"
                      >
                        {r.templateName}
                      </button>
                    </Td>
                    <Td>
                      <StatusBadge status={r.status} size="sm" />
                    </Td>
                    <Td>
                      {r.status === 'completed' && r.result?.fileId ? (
                        <Link to="/storage" className="text-brand-600 hover:underline dark:text-brand-400">
                          {r.result.rowCount != null ? `${r.result.rowCount} baris` : 'lihat berkas'}
                        </Link>
                      ) : r.status === 'failed' ? (
                        <span className="text-xs text-danger-500">{r.error}</span>
                      ) : (
                        <Badge tone="neutral" size="sm">
                          menunggu
                        </Badge>
                      )}
                    </Td>
                    <Td className="text-[var(--text-muted)]">{formatRelative(r.createdAt)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Card>
      )}

      {logTerbuka && (
        <ScriptRunLogDialog runId={logTerbuka} onClose={() => setLogTerbuka(null)} />
      )}
      <Modal
        open={periksaTerbuka}
        onClose={() => setPeriksaTerbuka(false)}
        size="lg"
        title="Folder ini belum siap dianalisa"
        description={hasilPeriksa?.folder?.nama}
      >
        <RincianPeriksa hasil={hasilPeriksa} />
      </Modal>

    </div>
  );
};
